import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'

import { RoomEvent } from 'livekit-client'

import { useMaybeRoomContext } from '@/src/context'
import { checkIsRealParticipant, getVisibleParticipants } from '@/src/helpers/visibleParticipants'

import { closeRoomPopupWorkerScript } from '@/workers/closeRoomPopup.worker'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

interface IWorkerMsg {
  isOpen?: boolean
  closeRoom?: boolean
  leftTime?: string
  message?: string
}

export const CloseRoomPopup = memo(function memoCloseRoomPopup() {
  const router = useRouter()
  const room = useMaybeRoomContext()
  const { t } = useTranslation()

  const { text_primary_color, url_log_out } = useAppSelector(selectBranding)
  const [isOpened, setIsOpened] = useState(false)
  const [leftTime, setLeftTime] = useState('1:00')

  const worker = useMemo(() => new Worker(closeRoomPopupWorkerScript), [])
  const audio = useMemo(() => new Audio('/sounds/close-room-popup.mp3'), [])

  const reloadPage = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault()
    worker.terminate()
    router.push(url_log_out || 'https://proofix.ru')
  }

  const handleClosePopup = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault()
    setIsOpened(false)
    setLeftTime('01:00')
    worker.postMessage({ hasClosedPopup: true })
  }

  useEffect(() => {
    if (!room) return
    if (room?.localParticipant?.isAgent || room?.localParticipant?.permissions?.recorder) return

    const postVisibleParticipants = (count: number) => {
      const isWaitingRoom = !checkIsRealParticipant(room?.localParticipant)
      worker.postMessage({ visibleParticipants: count, isWaitingRoom })
    }

    const handleCheck = () => {
      let countOfVisibleParticipants = 0

      const setVisibleParticipants = () => {
        const isWaitingRoom = !checkIsRealParticipant(room?.localParticipant)

        // +1 нужно, так как мы должны учитывать и себя самого
        countOfVisibleParticipants =
          getVisibleParticipants(room?.remoteParticipants)?.length + (isWaitingRoom ? 0 : 1)
      }

      setVisibleParticipants()

      setTimeout(() => {
        setVisibleParticipants()
        postVisibleParticipants(countOfVisibleParticipants)
      }, 500)

      return countOfVisibleParticipants
    }

    handleCheck()

    worker.onmessage = (workerMsg: MessageEvent<IWorkerMsg>) => {
      // console.log('Message: ', workerMsg.data)

      const countOfVisibleParticipants = handleCheck()

      if (typeof workerMsg.data?.isOpen === 'boolean') {
        setIsOpened((prev) => {
          if (!prev && workerMsg.data?.isOpen && countOfVisibleParticipants <= 1) {
            audio?.play()
            return true
          }

          if (countOfVisibleParticipants > 1) {
            return false
          }

          return prev
        })
      }

      if (countOfVisibleParticipants <= 1 && workerMsg.data?.closeRoom) {
        reloadPage()
      }

      if (workerMsg.data?.leftTime) {
        setLeftTime(workerMsg.data?.leftTime)
      }

      if (workerMsg.data?.message) {
        console.log('Message: ', workerMsg.data?.message)
      }
    }

    room
      ?.on(RoomEvent.Connected, handleCheck)
      ?.on(RoomEvent.ParticipantConnected, handleCheck)
      ?.on(RoomEvent.ParticipantPermissionsChanged, handleCheck)
      ?.on(RoomEvent.ParticipantDisconnected, handleCheck)

    return () => {
      room
        ?.off(RoomEvent.Connected, handleCheck)
        ?.off(RoomEvent.ParticipantConnected, handleCheck)
        ?.off(RoomEvent.ParticipantPermissionsChanged, handleCheck)
        ?.off(RoomEvent.ParticipantDisconnected, handleCheck)
    }
  }, [room, room?.remoteParticipants?.size, worker, audio])

  return (
    isOpened && (
      <div className="lk-toast-connection-state lk-toast close-room-popup">
        <h4 className="close-room-popup-title" style={{ color: `${text_primary_color}` }}>
          {t('closeRoomPopup.title')}
        </h4>

        <span className="close-room-popup-left-time">{leftTime}</span>

        <p className="close-room-popup-description" style={{ color: `${text_primary_color}` }}>
          {t('closeRoomPopup.description')}
        </p>

        <div className="close-room-popup-buttons">
          <button
            className="lk-disconnect-button"
            style={{ marginRight: '10px' }}
            onClick={reloadPage}
          >
            {t('closeRoomPopup.leaveButton')}
          </button>

          <button className="lk-button" onClick={handleClosePopup}>
            {t('closeRoomPopup.cancelButton')}
          </button>
        </div>
      </div>
    )
  )
})
